import { useState, useEffect, useRef, useContext } from 'react';
import { AppContext, DispatchContext } from '../AppContext';
import * as actions from '../actions/actions';
//const VERSION_CHECK_INTERVAL = 15 * 1000; // 15 seconds for testing
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

function getClientVersion() {
    const meta = document.querySelector('meta[name="version"]');
    return meta ? meta.getAttribute('content') : null;
}

export default function useVersionCheck() {
    const state = useContext(AppContext);
    const s = state.strings;
    const dispatch = useContext(DispatchContext);
    const intervalRef = useRef(null);
    const [prompted, setPrompted] = useState(false);

    useEffect(() => {
        // Only run when user is logged in
        if (!state.user.sessionId) return;

        const checkVersion = () => {
            if (prompted) return;

            const clientVersion = getClientVersion();
            if (!clientVersion) return;

            // Fetch version.json with cache-busting query param, relative to app root
            fetch(`/version.json?t=${Date.now()}`)
                .then(res => {
                    if (!res.ok) return null;
                    return res.json();
                })
                .then(data => {
                    if (!data || !data.version) return;
                    if (data.version !== clientVersion) {
                        setPrompted(true);
                        dispatch(actions.openConfirmationDialog({
                            title: s.versionCheck?.title || 'Update Available',
                            text: s.versionCheck?.description || 'A new version of PlagAware is available. Please reload the page to get the latest version.',
                            okLabel: s.versionCheck?.reload || 'Reload Now',
                            cancelLabel: s.versionCheck?.later || 'Later',
                            onConfirm: () => {
                                window.location.reload(true);
                            },
                            onCancel: () => {
                                // Allow re-prompting after the next interval
                                setPrompted(false);
                            },
                        }));
                    }
                })
                .catch(() => {
                    // Silently ignore fetch errors
                });
        };

        // Initial check after a short delay
        const initialTimeout = setTimeout(checkVersion, 10000);

        // Periodic checks
        intervalRef.current = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

        return () => {
            clearTimeout(initialTimeout);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [state.user.sessionId, prompted, s, dispatch]);
}
